import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createSchema } from '../api/schemas'
import { getSchemaTypes } from '../api/schemaTypes'

interface CreateSchemaModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: number
  panelId: number
}

export default function CreateSchemaModal({ isOpen, onClose, projectId, panelId }: CreateSchemaModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    schemaTypeId: 1 // По умолчанию "Главная"
  })

  const queryClient = useQueryClient()

  // Загружаем типы схем
  const { data: schemaTypes = [], isLoading: typesLoading } = useQuery({
    queryKey: ['schema-types'],
    queryFn: getSchemaTypes,
    enabled: isOpen
  })

  // Мутация для создания схемы
  const createSchemaMutation = useMutation({
    mutationFn: createSchema,
    onSuccess: (data) => {
      console.log('✅ Schema created successfully:', data)
      // Инвалидируем все запросы схем для этого проекта и панели
      queryClient.invalidateQueries({ queryKey: ['schemas', projectId, panelId] })
      queryClient.invalidateQueries({ queryKey: ['schemas', projectId] })
      queryClient.invalidateQueries({ queryKey: ['schemas'] })
      handleClose()
    },
    onError: (error) => {
      console.error('❌ Failed to create schema:', error)
    }
  })

  // Обработка ESC клавиши
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEsc)
      return () => document.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    createSchemaMutation.mutate({
      project_id: projectId,
      name: formData.name,
      description: formData.description || undefined,
      schema_type_id: formData.schemaTypeId,
      electrical_panel_id: panelId
    })
  }

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      schemaTypeId: 1
    })
    onClose()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4">Добавить схему</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Тип схемы
            </label>
            {typesLoading ? (
              <div className="text-center py-4 text-gray-500">Загрузка типов...</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {schemaTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, schemaTypeId: type.id })}
                    className={`p-3 text-left rounded-lg border-2 transition-all ${
                      formData.schemaTypeId === type.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-sm">{type.name}</div>
                    {type.description && (
                      <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название схемы
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите название схемы"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Описание схемы (необязательно)"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={createSchemaMutation.isPending || typesLoading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {createSchemaMutation.isPending ? 'Создание...' : 'Создать'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
